import React from 'react';
import {
  mutedTextRoleStyle,
  type TextRoleStyle,
} from './semanticTextRoles';

/**
 * WEB-926, позиция 5 — statement-agreement (референс AddMile: цитата-утверждение
 * в кавычках, серая строка «Do you agree with this statement?» и три круглые
 * реакции ✕ / 🤔 / ✓ с семантическими цветами; выбранная заливается светлым
 * тоном своего цвета).
 *
 * ЭТО ВОПРОС, А НЕ ОФОРМЛЕНИЕ. Клик пишет values[blockId] стабильным слагом
 * disagree|unsure|agree — эти значения автор ставит в условия ветвления, и они
 * же уходят в SCREEN_ANSWER (см. ANSWER_BLOCK_TYPES). Подписи под кнопками
 * менять можно свободно: они не участвуют в логике.
 *
 * Почему отдельный тип, а не режим likert_scale: там шкала ИНТЕНСИВНОСТИ с
 * ответом-номером («4» из 5), здесь троичная РЕАКЦИЯ. Один тип на две
 * семантики означал бы, что аналитика и правила ветвления не могут отличить
 * «3 из 5» от «иногда».
 */

export type StatementReaction = 'disagree' | 'unsure' | 'agree';

interface ReactionSpec {
  key: StatementReaction;
  glyph: string;
  /** Цвет реакции: ссылка на тему с фолбэком, собственной палитры блок не заводит. */
  color: string;
}

/**
 * Три реакции в порядке референса: отрицание слева, сомнение в центре,
 * согласие справа. Цвета семантические (красный/оранжевый/зелёный) — они несут
 * смысл ответа, поэтому не берутся из акцента пака.
 */
const REACTIONS: readonly ReactionSpec[] = [
  { key: 'disagree', glyph: '✕', color: 'var(--color-error, #ff4d4f)' },
  { key: 'unsure', glyph: '🤔', color: 'var(--color-warning, #faad14)' },
  { key: 'agree', glyph: '✓', color: 'var(--color-success, #52c41a)' },
];

interface StatementAgreementBlockProps {
  blockId: string;
  /** Утверждение (может быть rich-узлом — рендерит вызывающая сторона). */
  statement?: React.ReactNode;
  /** Серая строка-вопрос под утверждением. */
  prompt?: string;
  labels: Record<StatementReaction, string | undefined>;
  /** Absent = показывать среднюю реакцию (три кнопки). */
  showNeutral?: boolean;
  /**
   * WEB-1692: накладка на САМО УТВЕРЖДЕНИЕ (роль «главный текст»). Пустой
   * объект = сегодняшний вид: зашитые 22px и цвет поверхности блока.
   */
  titleStyle?: TextRoleStyle;
  /**
   * WEB-1692: накладка на ВТОРИЧНЫЕ ТЕКСТЫ — строку-вопрос и три подписи
   * реакций разом. Пустой объект = сегодняшний вид (14px/13px и приглушённые
   * переменные темы). Почему строка-вопрос отнесена сюда, а не к утверждению,
   * разобрано в semanticTextRoles.ts.
   */
  secondaryStyle?: TextRoleStyle;
  /** Текущий ответ из значений формы. */
  value: string;
  onSelect: (value: StatementReaction) => void;
}

const NO_ROLE_STYLE: TextRoleStyle = {};

export function StatementAgreementBlock({
  blockId,
  statement,
  prompt,
  labels,
  showNeutral,
  titleStyle = NO_ROLE_STYLE,
  secondaryStyle = NO_ROLE_STYLE,
  value,
  onSelect,
}: StatementAgreementBlockProps): React.ReactElement {
  // Приглушение невыбранных подписей появляется ТОЛЬКО вместе с авторским
  // цветом: без него разницу состояний по-прежнему несут переменные темы.
  const mutedLabel = mutedTextRoleStyle(secondaryStyle);
  const visible = REACTIONS.filter((r) => r.key !== 'unsure' || showNeutral !== false);

  return (
    <div
      data-testid="statement-agreement"
      data-block-id={blockId}
      style={{ marginBottom: 16, textAlign: 'center' }}
    >
      {statement && (
        <div
          data-testid="statement-text"
          style={{
            fontSize: 22,
            fontWeight: 700,
            lineHeight: 1.3,
            letterSpacing: '-0.4px',
            marginBottom: 8,
            // WEB-1692: слой автора спредится ПОСЛЕ прежних строк. Пусто =
            // `{}` — inline-стиль старой воронки не меняется ни на байт.
            ...titleStyle,
          }}
        >
          {statement}
        </div>
      )}
      {prompt && (
        <div
          data-testid="statement-prompt"
          style={{
            fontSize: 14,
            color: 'var(--color-text-secondary, #8c8c8c)',
            marginBottom: 20,
            ...secondaryStyle,
          }}
        >
          {prompt}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: 'var(--option-gap, 16px)',
        }}
      >
        {visible.map((reaction) => {
          const selected = value === reaction.key;
          const label = labels[reaction.key];
          return (
            <button
              key={reaction.key}
              type="button"
              data-testid={`statement-reaction-${reaction.key}`}
              data-selected={selected ? 'true' : 'false'}
              aria-pressed={selected}
              onClick={() => onSelect(reaction.key)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                padding: 0,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                font: 'inherit',
                color: 'inherit',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 26,
                  // Выбранная — светлая заливка своего цвета (приём AddMile).
                  // color-mix берёт тот же цвет, что и рамка: второй источник
                  // оттенка развёл бы рамку и заливку.
                  border: `var(--option-border-width, 2px) solid ${reaction.color}`,
                  background: selected
                    ? `color-mix(in srgb, ${reaction.color} 18%, transparent)`
                    : 'transparent',
                  color: reaction.color,
                  transition: 'background-color var(--transition-ms, 0ms) ease',
                }}
              >
                {reaction.glyph}
              </span>
              {label && (
                <span
                  data-testid={`statement-reaction-label-${reaction.key}`}
                  style={{
                    fontSize: 13,
                    maxWidth: 96,
                    color: selected
                      ? 'var(--color-text-primary, #1f1f1f)'
                      : 'var(--color-text-secondary, #8c8c8c)',
                    ...secondaryStyle,
                    // Авторский цвет ставится целиком, а невыбранные подписи
                    // приглушаются от него же — иначе одна настройка убрала бы
                    // единственный признак «что выбрано» в подписях.
                    ...(selected ? {} : mutedLabel),
                  }}
                >
                  {label}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
